import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts

import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';

// config
import { PATH_AFTER_LOGIN } from '../config';
// components
import LoadingScreen from '../components/LoadingScreen';
import '../sections/auth/register/register_style.css';
import { MobileReset } from '../sections/auth/reset-mob-password';
import { FrontAccount } from '../sections/auth/front-account';
import Login2 from '../pages/auth/Login2';


// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: 'sign-in', element: <Login /> },
        { path: 'sign-up', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'reset-mob-password', element: <MobileReset/> },
        { path: 'front-account', element: <FrontAccount/> },
        { path: 'verify/:id', element: <VerifyCode /> },
        { path: 'login2/:radio', element:<Login2/> },
      ],
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        // AUTHOR 
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'app', element: <GeneralApp /> },
        { path: 'author', element: <GeneralAuthor /> },
        { path: 'book', element: <GeneralBook /> },
        { path: 'addbook', element: <GeneralAddBook /> },
        { path: 'addbook/:id', element: <GeneralAddBook /> },
        { path: 'bookreview', element: <GeneralBookReview /> },
        { path: 'authorreview', element: <GeneralAuthorReview /> },
       
        // COACH
        { path: 'app', element: <GeneralApp /> },
        { path: 'coach', element: <GeneralCoach /> },
        { path: 'audio', element: <GeneralAudio /> },
        { path: 'addaudio', element: <AddAudio /> },
        { path: 'addaudio/:id', element: <AddAudio /> },
        { path: 'addvideo', element: <AddVideo /> },
        { path: 'addvideo/:id', element: <AddVideo /> },
        { path: 'video', element: <GeneralVideo /> },
        { path: 'coachreview', element: <CoachReview /> },
        { path: 'audioreview', element: <AudioReview /> },
        { path: 'videoreview', element: <VideoReview /> },
        {
          path: 'user',
          children: [
            { element: <Navigate to="/dashboard/user/profile" replace />, index: true },
            { path: 'profile', element: <UserProfile /> },
            { path: 'cards', element: <UserCards /> },
            { path: 'list', element: <UserList /> },
            { path: 'new', element: <UserCreate /> },
            { path: ':name/edit', element: <UserCreate /> },
            { path: 'account', element: <UserAccount /> },
          ],
        },
      
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
       
        { path: '500', element: <Page500 /> },
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: <MainLayout />,
      children: [
        { element: <Register />, index: true },
      
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import('../pages/auth/Login')));

const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
;

const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));
// Dashboard
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));
const GeneralAuthor = Loadable(lazy(() => import('../pages/dashboard/GeneralAuthor')));
const GeneralBook = Loadable(lazy(() => import('../pages/dashboard/GeneralBook')));
const GeneralAddBook = Loadable(lazy(() => import('../pages/dashboard/GeneralAddBook')));
const GeneralBookReview = Loadable(lazy(() => import('../pages/dashboard/GeneralBookReview')));
const GeneralAuthorReview = Loadable(lazy(() => import('../pages/dashboard/GeneralAuthorReview')));
const GeneralCoach = Loadable(lazy(() => import('../pages/dashboard/GeneralCoach')));
const GeneralAudio = Loadable(lazy(() => import('../pages/dashboard/GeneralAudio')));
const AddAudio = Loadable(lazy(() => import('../pages/dashboard/AddAudio')));
const GeneralVideo = Loadable(lazy(() => import('../pages/dashboard/GeneralVideo')));
const AddVideo = Loadable(lazy(() => import('../pages/dashboard/AddVideo')));
const CoachReview = Loadable(lazy(() => import('../pages/dashboard/CoachReview')));
const AudioReview = Loadable(lazy(() => import('../pages/dashboard/AudioReview')));
const VideoReview = Loadable(lazy(() => import('../pages/dashboard/VideoReview')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
const UserProfile = Loadable(lazy(() => import('../pages/dashboard/UserProfile')));
const UserCards = Loadable(lazy(() => import('../pages/dashboard/UserCards')));
const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList')));
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));
const UserCreate = Loadable(lazy(() => import('../pages/dashboard/UserCreate')));
